import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { HOME_PROMO_ADS, HOME_PROMO_AD_AUTOPLAY_MS, HOME_PROMO_AD_FRAME_HEIGHT, HOME_PROMO_AD_FRAME_WIDTH } from '../constants/homePromoAds';
import { useI18n } from '../i18n';
import homeStyles from '../pages/HomePage.module.css';
import styles from './HomePromoAdsCarousel.module.css';

const DRAG_CLICK_THRESHOLD_PX = 6;
const AUTOPLAY_RESUME_MS = 8000;

export function HomePromoAdsCarousel() {
  const { t } = useI18n();
  const title = t('home.promoAds');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const autoplayPausedRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);
  const dragState = useRef({
    active: false,
    startX: 0,
    startScroll: 0,
    moved: false,
  });
  const canScroll = HOME_PROMO_ADS.length > 1;

  const pauseAutoplay = useCallback(() => {
    autoplayPausedRef.current = true;
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = window.setTimeout(() => {
      autoplayPausedRef.current = false;
    }, AUTOPLAY_RESUME_MS);
  }, []);

  const syncActiveFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || HOME_PROMO_ADS.length <= 1) {
      setActiveIndex(0);
      return;
    }
    const width = el.clientWidth;
    if (width <= 0) {
      return;
    }
    const idx = Math.round(el.scrollLeft / width);
    setActiveIndex(Math.min(Math.max(idx, 0), HOME_PROMO_ADS.length - 1));
  }, []);

  const scrollToIndex = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const width = el.clientWidth;
    const left = index * width;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({ left, behavior });
    } else {
      el.scrollLeft = left;
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => syncActiveFromScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncActiveFromScroll()) : null;
    ro?.observe(el);
    syncActiveFromScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, [syncActiveFromScroll]);

  useEffect(() => {
    if (!canScroll) {
      return;
    }
    const id = window.setInterval(() => {
      if (autoplayPausedRef.current || document.hidden) {
        return;
      }
      const el = scrollRef.current;
      if (!el || el.clientWidth <= 0) {
        return;
      }
      const current = Math.round(el.scrollLeft / el.clientWidth);
      const next = (current + 1) % HOME_PROMO_ADS.length;
      scrollToIndex(next);
    }, HOME_PROMO_AD_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [canScroll, scrollToIndex]);

  useEffect(
    () => () => {
      if (resumeTimerRef.current !== null) {
        window.clearTimeout(resumeTimerRef.current);
      }
    },
    [],
  );

  const onTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canScroll || event.button !== 0) {
      return;
    }
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    pauseAutoplay();
    dragState.current = {
      active: true,
      startX: event.clientX,
      startScroll: el.scrollLeft,
      moved: false,
    };
    setIsDragging(true);
    el.setPointerCapture(event.pointerId);
  };

  const onTrackPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.active) {
      return;
    }
    const dx = event.clientX - dragState.current.startX;
    if (Math.abs(dx) > DRAG_CLICK_THRESHOLD_PX) {
      dragState.current.moved = true;
    }
    el.scrollLeft = dragState.current.startScroll - dx;
  };

  const endTrackDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !dragState.current.active) {
      return;
    }
    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    dragState.current.active = false;
    setIsDragging(false);
    if (dragState.current.moved && el.clientWidth > 0) {
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      scrollToIndex(Math.min(Math.max(idx, 0), HOME_PROMO_ADS.length - 1));
    }
    pauseAutoplay();
  };

  const onDotClick = (index: number) => {
    pauseAutoplay();
    scrollToIndex(index);
  };

  const rootStyle = {
    '--promo-frame-pad': `${(HOME_PROMO_AD_FRAME_HEIGHT / HOME_PROMO_AD_FRAME_WIDTH) * 100}%`,
  } as CSSProperties;

  return (
    <section className={homeStyles.section} aria-label={title}>
      <div className={homeStyles.sectionHead}>
        <h2 className={homeStyles.sectionTitle}>{title}</h2>
      </div>
      <div className={styles.root} style={rootStyle}>
        <div
          ref={scrollRef}
          className={[styles.track, isDragging ? styles.trackDragging : ''].filter(Boolean).join(' ')}
          aria-roledescription="carousel"
          aria-label={title}
          onPointerDown={onTrackPointerDown}
          onPointerMove={onTrackPointerMove}
          onPointerUp={endTrackDrag}
          onPointerCancel={endTrackDrag}
        >
          {HOME_PROMO_ADS.map((ad, index) => (
            <article key={ad.id} className={styles.slide} data-carousel-item="true">
              <div className={styles.slideFrame}>
                <div className={styles.slideMedia}>
                  <img
                    src={ad.imageSrc}
                    alt={t(ad.labelKey)}
                    className={ad.cropToFrame === false ? styles.billboardImage : styles.imageCover}
                    style={ad.objectPosition ? { objectPosition: ad.objectPosition } : undefined}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    draggable={false}
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
        {canScroll ? (
          <div className={styles.dots} role="tablist" aria-label={title}>
            {HOME_PROMO_ADS.map((ad, index) => (
              <button
                key={ad.id}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={t(ad.labelKey)}
                className={[styles.dot, index === activeIndex ? styles.dotActive : ''].filter(Boolean).join(' ')}
                onClick={() => onDotClick(index)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}
